import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AudioPlayer from './AudioPlayer';
import { X, Headphones, Play, Loader2, AlertCircle } from 'lucide-react';
import { BACKEND_URL } from 'apiConfig';

const PlaylistModal = ({ playlist, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!playlist || !playlist.items) return null;

  const readyItems = playlist.items.filter(i => i.status === 'ready' && i.audio_url);
  const current = readyItems[currentIndex];

  const handleNext = () => {
    if (currentIndex < readyItems.length - 1) setCurrentIndex(currentIndex + 1);
  };
  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  const handleEnded = () => {
    if (currentIndex < readyItems.length - 1) setCurrentIndex(currentIndex + 1);
  };

  // audio_url from the API is either a full S3 presigned URL (https://...)
  // or a relative path (/api/audio/...). Only prepend apiBase for relative paths.
  const resolveAudioUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden" data-testid="playlist-modal">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Headphones className="h-5 w-5 text-blue-600" />
            Commute Mode
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">{playlist.counts?.ready || 0}/{playlist.counts?.total || 0} ready</Badge>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose} data-testid="playlist-modal-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {current ? (
            <AudioPlayer
              src={resolveAudioUrl(current.audio_url)}
              title={current.title}
              onEnded={handleEnded}
              onNext={currentIndex < readyItems.length - 1 ? handleNext : null}
              onPrev={currentIndex > 0 ? handlePrev : null}
            />
          ) : (
            <div className="text-center py-4 text-sm text-gray-500">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              No audio ready yet. Generate audio for articles first.
            </div>
          )}

          {/* Queue list */}
          <div className="max-h-48 overflow-y-auto space-y-1">
            {playlist.items.map((item, idx) => {
              const readyIdx = readyItems.indexOf(item);
              const isPlaying = readyIdx === currentIndex && item.status === 'ready';
              return (
                <div
                  key={item.pmid}
                  className={`flex items-center gap-2 py-1.5 px-2 rounded text-sm ${isPlaying ? 'bg-blue-50 border border-blue-200' : ''}`}
                  onClick={() => { if (readyIdx >= 0) setCurrentIndex(readyIdx); }}
                  style={{ cursor: item.status === 'ready' ? 'pointer' : 'default' }}
                >
                  {item.status === 'ready' ? (
                    <Play className={`h-3.5 w-3.5 shrink-0 ${isPlaying ? 'text-blue-600' : 'text-gray-400'}`} />
                  ) : item.status === 'pending' ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 text-amber-500 animate-spin" />
                  ) : (
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                  )}
                  <span className={`responsive-title truncate ${isPlaying ? 'font-medium text-blue-900' : 'text-gray-700'}`}>
                    {item.title}
                  </span>
                  <Badge variant="outline" className="text-[9px] ml-auto shrink-0">{item.status}</Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlaylistModal;
